import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Guid } from 'guid-typescript';
import { DashboardNewsArticleViewModel } from 'root/mibp-openapi-gen/models';
import { BroadcastService, LogService, MibpLogger, SignalR_Rendition } from 'root/services';
import { GoogleTagManagerService } from 'root/services/google-tag-manager-service/google-tag-manager.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from 'root/shared/components/status/loader/loader.component';
import { ImageComponent } from 'root/components/image/image.component';
import { ResourceStringComponent } from 'root/shared/components/localization/resource-string/resource-string.component';
import { biDashboardFnHomeReadNews } from "root/mibp-openapi-gen/fn/bi-dashboard/bi-dashboard-fn-home-read-news";
import { MibpApiService } from "root/mibp-openapi-gen/mibp-api.service";

@Component({
  selector: 'mibp-news-carousel',
  templateUrl: './news-carousel.component.html',
  styleUrls: ['./news-carousel.component.scss'],
  animations: [
    trigger('carouselAnimation', [
      transition('void => *', [
        style({ opacity: 0 }),
        animate('1000ms', style({ opacity: 1 }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, LoaderComponent, ImageComponent, ResourceStringComponent]
})

export class MibpNewsCarouselComponent implements OnInit, OnDestroy {
    private mibpApiService = inject(MibpApiService);
  private renderer = inject(Renderer2);
  private broadcast = inject(BroadcastService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  @ViewChild('carousel') carouselElement: ElementRef;
  @Input() newsArticles: DashboardNewsArticleViewModel[];
  slides: Slide[] = [];
  isLoading = true;

  currentSlide = 0;
  imageNotFound: boolean;
  base64String: SafeUrl;
  log: MibpLogger;
  renditionSize = SignalR_Rendition.Large;
  screenSizeSub?: Subscription;
  loadedImage= false;
  heightAdjustedBy = 0;

  constructor() {
    const logger = inject(LogService);

    this.log = logger.withPrefix('news-carousel');
  }

  ngOnInit(): void {
    this.loadSlides();
    this.screenSizeSub = this.broadcast.screenSize
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.updateCarouselHeightBasedOnCurrentImage();
      });
  }

  imageLoaded(){
    this.loadedImage = true;
    this.onImageLoad();
  }

  ngOnDestroy(): void {
    this.screenSizeSub?.unsubscribe();
  }

  onPreviousClick() {
    this.loadedImage = false;
    const previous = this.currentSlide - 1;
    this.currentSlide = previous < 0 ? this.slides.length - 1 : previous;
  }

  onNextClick() {
    this.loadedImage = false;
    const next = this.currentSlide + 1;
    this.currentSlide = next === this.slides.length ? 0 : next;
  }

  async onReadMoreClick(articleId: string) {
    await firstValueFrom(this.mibpApiService.invoke(biDashboardFnHomeReadNews));
    this.router.navigate([`news/${articleId}`], { relativeTo: this.activatedRoute });
  }

  onImageClick(articleId: string) {
    this.router.navigate([`news/${articleId}`], { relativeTo: this.activatedRoute });
  }

  loadSlides() {
    for (let index = 0; index < this.newsArticles?.length; index++) {
      if (index >= 10) {
        return; //We only suport 10 slides in carousel
      }
      const article = this.newsArticles[index];
      const slideToAdd: Slide = {};

      slideToAdd.articleId = article.id;
      slideToAdd.articleTitle = article.titleDefaultValue;
      slideToAdd.userFileId = Guid.parse(article.thumbnailUserFileId);
      slideToAdd.articlePreamble = article.preambleDefaultValue;

      this.slides = [...this.slides, slideToAdd];
      this.isLoading = false;
    }
  }

  async navigateToSlide(selectedSlide: number) {
    this.currentSlide = selectedSlide;
  }

  onImageLoad(): void {
    this.updateCarouselHeightBasedOnCurrentImage();
  }

  updateCarouselHeightBasedOnCurrentImage(): void {
    if( this.heightAdjustedBy < (this.slides[this.currentSlide].articlePreamble.length / 50)) {
      const newCalculatedHeight = this.carouselElement.nativeElement.clientHeight + 15 * (this.slides[this.currentSlide].articlePreamble.length / 50);
      this.renderer.setStyle(this.carouselElement.nativeElement, 'minHeight', newCalculatedHeight + 'px');
      this.heightAdjustedBy = (this.slides[this.currentSlide].articlePreamble.length / 50);
    }
  }

  determineNewHeight(originalWidth: number, originalHeight: number,  newWidth: number): number {
    return (originalHeight / originalWidth) * newWidth;
  }

}

export interface Slide {
  articleId?: string,
  articleTitle?: string;
  articlePreamble?: string;
  userFileId?: Guid,
  url?: string | null;
  imageNotFound?: boolean | null;

}
